/**
 * Convidar para uma família que já existe (PO, 20/09/2026).
 *
 * Até aqui só dava para convidar no ato de criar a família; quem criou
 * convidando uma pessoa não tinha como chamar a segunda. Esta folha fecha o
 * buraco pelo mesmo caminho da criação: lista de quem está disponível, várias
 * pessoas de uma vez, e cada uma recebe um convite com aceite — ninguém entra
 * sem responder. Sem limite de convites por família.
 *
 * Disponível = está na viagem, não está em família nenhuma e não tem convite
 * pendente para esta. Quem não pode ser convidado não aparece, em vez de
 * aparecer e falhar depois.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { MembroDaViagem } from '../../trip/TripContext';
import { Sheet } from '../../ui/Sheet';
import { Card } from '../../ui/primitives';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { useItinerary } from '../roteiro/store';
import { type FamilyView, GrupoError, inviteToFamily } from './api';

export function disponiveisParaConvite(familia: FamilyView, membros: MembroDaViagem[], meuMemberId: string) {
  return membros.filter(
    (m) => m.memberId !== meuMemberId && !m.familyId && !familia.pendingInviteeIds.includes(m.memberId),
  );
}

export function ConvidarParaFamiliaSheet({
  tripId,
  familia,
  membros,
  meuMemberId,
  onFechar,
}: {
  tripId: string;
  familia: FamilyView;
  membros: MembroDaViagem[];
  meuMemberId: string;
  onFechar: () => void;
}) {
  const cliente = useQueryClient();
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const disponiveis = disponiveisParaConvite(familia, membros, meuMemberId);

  const enviar = useMutation({
    // Um convite por pessoa, em sequência: se o terceiro falhar, os dois
    // primeiros já valem e a mensagem de erro aponta para quem faltou.
    mutationFn: async () => {
      for (const memberId of escolhidos) {
        try {
          await inviteToFamily(tripId, familia.id, memberId);
        } catch (e) {
          const nome = membros.find((m) => m.memberId === memberId)?.name ?? 'essa pessoa';
          throw new GrupoError((e as GrupoError).code ?? 'erro', `${nome}: ${(e as Error).message}`);
        }
      }
      return escolhidos.length;
    },
    onSuccess: (n) => {
      void cliente.invalidateQueries({ queryKey: ['grupo', tripId] });
      useItinerary.getState().showToast(n === 1 ? 'Convite enviado.' : `${n} convites enviados.`, 4000);
      onFechar();
    },
    onError: () => {
      void cliente.invalidateQueries({ queryKey: ['grupo', tripId] });
    },
  });

  return (
    <Sheet titleId="convidar-familia-titulo" onFechar={onFechar} testId="convidar-familia-sheet">
      <h2 className="sheet__title" id="convidar-familia-titulo">Convidar para a {familia.name}</h2>
      <Card variant="surf">
        {disponiveis.length === 0 ? (
          <p className="auth-note" data-testid="convidar-familia-vazio">
            Ninguém disponível para convidar agora — todo mundo já está em uma família ou tem convite aguardando resposta.
          </p>
        ) : (
          disponiveis.map((m) => {
            const marcado = escolhidos.includes(m.memberId);
            return (
              <label className="fin-nova__pessoa" key={m.memberId} data-testid="convidar-familia-pessoa">
                <input
                  type="checkbox"
                  checked={marcado}
                  onChange={() =>
                    setEscolhidos((atual) => (marcado ? atual.filter((id) => id !== m.memberId) : [...atual, m.memberId]))
                  }
                />
                <FotoAvatar nome={m.name} url={m.avatarUrl} cor={m.familyColor} tamanho={24} />
                <span>{m.name}</span>
              </label>
            );
          })
        )}
      </Card>
      <p className="auth-note">Cada pessoa recebe um convite e decide — ninguém entra sem aceitar.</p>
      {enviar.isError && (
        <p className="auth-error" role="alert">
          {(enviar.error as GrupoError).message}
        </p>
      )}
      <div className="fin__ctas">
        <button type="button" className="nv-btn nv-btn--secondary" onClick={onFechar}>
          Cancelar
        </button>
        <button
          type="button"
          className="nv-btn nv-btn--primary"
          disabled={escolhidos.length === 0 || enviar.isPending}
          onClick={() => enviar.mutate()}
          data-testid="convidar-familia-enviar"
        >
          {enviar.isPending
            ? 'Enviando…'
            : escolhidos.length <= 1
              ? 'Enviar convite'
              : `Enviar ${escolhidos.length} convites`}
        </button>
      </div>
    </Sheet>
  );
}
